/*!
 * @license
 * Copyright Squiz Australia Pty Ltd. All Rights Reserved.
 */

import clsx from 'clsx';
import Prism from 'prismjs';
import 'prismjs/components/prism-jsx.min.js';
import 'prismjs/components/prism-scss.min.js';
import 'prismjs/components/prism-tsx.min.js';
import 'prismjs/components/prism-typescript';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ICON_CONTENT_COPY, ICON_DONE_ALL } from '../../Icons';
import { SdsIcon } from '../SdsIcon';
import { CODE_SNIPPET_STRINGS } from './CodeSnippet.constants';
import { CodeSnippetProps } from './CodeSnippet.props';

export const CodeSnippet = ({ title, code, classes, languageHighlight }: CodeSnippetProps): JSX.Element => {
  const [copied, setCopied] = useState(false);
  const { t } = useTranslation();
  const labels = {
    copiedToClipboard: t(CODE_SNIPPET_STRINGS.copiedToClipboard),
    copyCode: t(CODE_SNIPPET_STRINGS.copyCode),
  };
  const copyToClipboard = (): void => {
    setCopied(true);
    navigator.clipboard.writeText(code);
    setTimeout(() => setCopied(false), 3000);
    return;
  };
  const codeRef = useRef<HTMLPreElement>(null);

  useEffect(() => {
    languageHighlight && Prism.highlightAll();
  }, [languageHighlight]);
  return (
    <div className={clsx(`sds-code-snippet`, classes)}>
      <div className="sds-code-snippet__header">
        {title && <span className="sds-code-snippet__title">{title}</span>}
        <button className="sds-code-snippet__copy-btn" onClick={(): void => copyToClipboard()}>
          <SdsIcon svg={copied ? ICON_DONE_ALL : ICON_CONTENT_COPY} />
          {copied ? labels.copiedToClipboard : labels.copyCode}
        </button>
      </div>
      <pre ref={codeRef}>
        <code
          className={clsx(`sds-code-snippet__code`, {
            [`language-${languageHighlight}`]: languageHighlight,
          })}
        >
          {code}
        </code>
      </pre>
    </div>
  );
};
